/**
 * Performance reviews and 1:1s, mirroring the backend `performance` module.
 * Company portal: `companies/:companyId/performance/...`.
 * Employee portal: `employee/performance/...`.
 */
import type { ApiResponse, GetListResponse } from './generic';

// ─── Enums ─────────────────────────────────────────────────────────────────

export enum ReviewCycleStatus {
  DRAFT = 'DRAFT',
  ACTIVE = 'ACTIVE',
  LOCKED = 'LOCKED',
}

export enum ReviewerType {
  SELF = 'SELF',
  MANAGER = 'MANAGER',
  PEER = 'PEER',
  /**
   * "Team" (spec 2026-10-29 upward feedback, U1): a manager's reports about
   * the manager. Always anonymous: no screen ever shows who answered what.
   */
  UPWARD = 'UPWARD',
}

export enum ReviewSubmissionStatus {
  NOT_STARTED = 'NOT_STARTED',
  IN_PROGRESS = 'IN_PROGRESS',
  SUBMITTED = 'SUBMITTED',
}

/** Derived by the backend from submissions + share/acknowledge dates. */
export enum ReviewStatus {
  NOT_STARTED = 'NOT_STARTED',
  IN_PROGRESS = 'IN_PROGRESS',
  READY_TO_SHARE = 'READY_TO_SHARE',
  SHARED = 'SHARED',
  ACKNOWLEDGED = 'ACKNOWLEDGED',
}

export enum ReviewQuestionType {
  RATING = 'RATING',
  TEXT = 'TEXT',
  MULTIPLE_CHOICE = 'MULTIPLE_CHOICE',
  /** The reviewee's goals, read-only with a comment each (PF-20). */
  GOALS = 'GOALS',
}

export enum ReviewPeerNominator {
  ADMIN = 'ADMIN',
  MANAGER = 'MANAGER',
  REVIEWEE = 'REVIEWEE',
}

/**
 * What a people search is for, when that changes who may be picked: starting
 * a 1:1 limits the picker to the caller's reporting subtree.
 */
export enum PeopleSearchPurpose {
  OneOnOne = 'one_on_one',
}

export enum OneOnOneCadence {
  WEEKLY = 'WEEKLY',
  BIWEEKLY = 'BIWEEKLY',
  MONTHLY = 'MONTHLY',
  NONE = 'NONE',
}

export enum OneOnOneMeetingStatus {
  UPCOMING = 'UPCOMING',
  COMPLETED = 'COMPLETED',
  SKIPPED = 'SKIPPED',
}

export enum OneOnOneRole {
  MANAGER = 'MANAGER',
  EMPLOYEE = 'EMPLOYEE',
}

/** What a cycle is for, set from its template (PF-9). */
export enum ReviewCycleKind {
  ANNUAL = 'annual',
  MID_YEAR = 'mid_year',
  QUARTERLY = 'quarterly',
  PROBATION = 'probation',
  AD_HOC = 'ad_hoc',
  /** Team feedback on managers (U1): reviewees are managers, reviewers their reports. */
  UPWARD = 'upward',
}

/** PF-14: company (HR), team (a manager's subtree) or individual. */
export enum GoalLevel {
  COMPANY = 'company',
  TEAM = 'team',
  INDIVIDUAL = 'individual',
}

/** PF-15: percent (0-100), number (start, target, unit) or done. */
export enum GoalMeasure {
  PERCENT = 'percent',
  NUMBER = 'number',
  DONE = 'done',
}

export enum GoalStatus {
  ON_TRACK = 'on_track',
  AT_RISK = 'at_risk',
  OFF_TRACK = 'off_track',
}

export enum GoalOutcome {
  ACHIEVED = 'achieved',
  NOT_ACHIEVED = 'not_achieved',
  CANCELLED = 'cancelled',
}

/** PF-19: the company's goal check-in reminder. */
export enum GoalReminderCadence {
  MONTHLY = 'monthly',
  BIWEEKLY = 'biweekly',
  OFF = 'off',
}

/**
 * Where the self review stands for the manager reviewer (PF-4): the manager
 * may draft at any time and submit once it is SUBMITTED or MISSED.
 */
export enum SelfReviewState {
  NONE = 'NONE',
  PENDING = 'PENDING',
  MISSED = 'MISSED',
  SUBMITTED = 'SUBMITTED',
}

// ─── People ────────────────────────────────────────────────────────────────

/** How any person appears on a performance screen. `id` is always the
 *  employee id: managers are employees too (PF-2). */
export interface PerformancePerson {
  id: string;
  userId: string;
  firstName: string;
  lastName: string;
  email: string | null;
  /** No email (spec 2026-10-15): nothing sent to them will arrive. */
  hasNoEmail?: boolean;
  profileImage: string | null;
  designation: string | null;
  department: string | null;
}

/**
 * Why someone may run (host) an employee's 1:1s: a manager above them, or HR
 * (`performance_review.create` with the employee's branch). Mirror of the
 * backend `OneOnOneHostKind`.
 */
export enum OneOnOneHostKind {
  MANAGER = 'manager',
  HR = 'hr',
}

/** One choice of the "Run by" picker (`one-on-one-pairs/managers`). */
export interface OneOnOneHost extends PerformancePerson {
  hostKind: OneOnOneHostKind;
}

// ─── Review forms ──────────────────────────────────────────────────────────

/**
 * What a choice of a "Probation decision" question means (mirror of the
 * backend's `ProbationDecisionOutcome`, v2 spec V2-5).
 */
export enum ProbationDecisionOutcome {
  NONE = 'none',
  CONFIRM = 'confirm',
  EXTEND = 'extend',
  END = 'end',
}

export interface ReviewQuestionOption {
  id: string;
  label: string;
  /** On a probation decision question only: what this choice means. */
  probationOutcome?: ProbationDecisionOutcome;
}

export interface ReviewQuestion {
  id: string;
  type: ReviewQuestionType;
  /** Default wording. In a reviewer-specific form this is already that
   *  reviewer's wording. */
  title: string;
  /** Per-reviewer wording, e.g. "How did you..." vs "How did they...". */
  titles?: Partial<Record<ReviewerType, string>>;
  description?: string;
  required: boolean;
  reviewerTypes: ReviewerType[];
  scaleMax?: number;
  scaleMinLabel?: string;
  scaleMaxLabel?: string;
  /** One label per point of the scale, lowest first (PF-7). */
  scaleLabels?: string[];
  /** RATING only: an optional comment beside the rating, in the answer's
   *  `text` (Modulmax probation form, 2026-10-28). */
  commentEnabled?: boolean;
  options?: ReviewQuestionOption[];
  /** MULTIPLE_CHOICE only: the manager's probation decision, one per form;
   *  an `extend` choice asks for the answer's `date` (V2-5). */
  probationDecision?: boolean;
  visibleToReviewee: boolean;
}

export interface ReviewFormSection {
  id: string;
  title: string;
  description?: string;
  questions: ReviewQuestion[];
}

export interface ReviewForm {
  sections: ReviewFormSection[];
}

/** One goal in a GOALS answer; submitted ones carry the snapshot (A29). */
export interface ReviewGoalAnswer {
  goalId: string;
  comment?: string | null;
  title?: string;
  progress?: number;
  status?: GoalStatus;
  outcome?: GoalOutcome | null;
}

/** Longest comment kept beside a rating (mirror of the backend's). */
export const RATING_COMMENT_MAX_LENGTH = 2000;

export interface ReviewAnswer {
  questionId: string;
  rating?: number | null;
  /** TEXT: the answer. RATING with `commentEnabled`: the optional comment. */
  text?: string | null;
  optionId?: string | null;
  /** A probation decision answered "extend": the new end date, YYYY-MM-DD,
   *  later than today (V2-5). */
  date?: string | null;
  /** GOALS only. */
  goals?: ReviewGoalAnswer[] | null;
}

/** A goal a GOALS question shows while the review is being written. */
export interface ReviewGoalItem {
  goalId: string;
  title: string;
  progress: number;
  status: GoalStatus;
  outcome: GoalOutcome | null;
  dueDate: string | null;
}

export interface SaveReviewAnswersPayload {
  answers: ReviewAnswer[];
  /** Submit instead of saving a draft. */
  submit?: boolean;
}

/** One reviewer's answers, with the form exactly as that reviewer saw it. An
 *  anonymous peer has `reviewer: null` and a synthetic `id`. */
export interface ReviewSubmissionBlock {
  id: string;
  reviewerType: ReviewerType;
  status: ReviewSubmissionStatus;
  submittedAt: string | null;
  score: number | null;
  reviewer: PerformancePerson | null;
  form: ReviewForm;
  answers: ReviewAnswer[];
  /**
   * The employee's self answers from the self evaluation a manager
   * evaluation is linked to: read-only, shown with that cycle's form.
   */
  linkedCycle?: { id: string; title: string };
}

/** A block as the reviewee receives it: never a computed score (PF-5). */
export type RevieweeSubmissionBlock = Omit<ReviewSubmissionBlock, 'score'>;

// ─── Templates ─────────────────────────────────────────────────────────────

export interface ReviewLibraryTemplate {
  key: string;
  /** The kind a cycle started from this template takes (PF-9). */
  kind: ReviewCycleKind;
  name: string;
  description: string;
  form: ReviewForm;
}

export interface ReviewCustomTemplate {
  id: string;
  name: string;
  description: string | null;
  form: ReviewForm;
  createdAt: string;
  updatedAt: string;
}

export interface ReviewTemplates {
  library: ReviewLibraryTemplate[];
  custom: ReviewCustomTemplate[];
}

export interface ReviewTemplatePayload {
  name: string;
  description?: string | null;
  form: ReviewForm;
}

/** A rename sends only the name and description; an edit sends the form. */
export type ReviewTemplateUpdatePayload = Partial<ReviewTemplatePayload>;

// ─── Cycles ────────────────────────────────────────────────────────────────

/**
 * Who fills a one-time evaluation (spec 2026-10-29 one reviewer per
 * evaluation). Same values as an automatic evaluation's "Who fills it"
 * (`EvaluationProgramReviewer`), plus colleagues and the team.
 */
export enum EvaluationReviewer {
  MANAGER = 'manager',
  SELF = 'self',
  PEER = 'peer',
  TEAM = 'team',
}

/** The reviewer type of the answers each reviewer writes. */
export const EVALUATION_REVIEWER_TYPE: Record<
  EvaluationReviewer,
  ReviewerType
> = {
  [EvaluationReviewer.MANAGER]: ReviewerType.MANAGER,
  [EvaluationReviewer.SELF]: ReviewerType.SELF,
  [EvaluationReviewer.PEER]: ReviewerType.PEER,
  [EvaluationReviewer.TEAM]: ReviewerType.UPWARD,
};

export interface ReviewCycleSettings {
  description: string | null;
  periodStart: string | null;
  periodEnd: string | null;
  /**
   * Who fills it. The three booleans below are derived from it (exactly one
   * true, none for the team). Null only on a cycle launched with several
   * reviewer types before 2026-10-29.
   */
  reviewer: EvaluationReviewer | null;
  /** A manager evaluation: the self evaluation its manager reads alongside. */
  linkedSelfCycleId: string | null;
  selfReviewEnabled: boolean;
  managerReviewEnabled: boolean;
  peerReviewEnabled: boolean;
  selfDueDate: string | null;
  peerDueDate: string | null;
  managerDueDate: string | null;
  autoLockDate: string | null;
  peerAnonymous: boolean;
  sharePeerFeedback: boolean;
  revieweeNominatesPeers: boolean;
  peersPerReviewee: number;
  requireAcknowledgement: boolean;
  /** Team feedback (kind `upward`): when the reports' answers are due. */
  upwardDueDate: string | null;
  /** Team feedback: indirect reports answer too (U1 option). */
  upwardIncludeIndirect: boolean;
}

export interface ReviewCycle extends ReviewCycleSettings {
  id: string;
  title: string;
  status: ReviewCycleStatus;
  kind: ReviewCycleKind;
  launchedAt: string | null;
  lockedAt: string | null;
  createdAt: string;
  updatedAt: string;
  /** The automatic evaluation that created it; null = a one-time cycle. */
  programId: string | null;
  programName: string | null;
}

export interface LinkedCycleBrief {
  id: string;
  title: string;
  status: ReviewCycleStatus;
}

export type ReviewerTypeCounts = Record<
  ReviewerType,
  { total: number; submitted: number }
>;

export interface ReviewCycleListItem extends ReviewCycle {
  participantCount: number;
  sharedCount: number;
  completion: { total: number; submitted: number };
}

export interface ReviewCycleDetail extends ReviewCycle {
  form: ReviewForm;
  /** A manager evaluation's linked self evaluation. */
  linkedSelfCycle: LinkedCycleBrief | null;
  /** A self evaluation: the manager evaluations that show it. */
  linkedManagerCycles: LinkedCycleBrief[];
  stats: {
    participantCount: number;
    missingManagerCount: number;
    byType: ReviewerTypeCounts;
    byStatus: Record<ReviewStatus, number>;
  };
}

/** Create/update body. On an active cycle only the title, description, period,
 *  due dates, auto-lock date and peers-per-reviewee may be sent. */
export type ReviewCyclePayload = Partial<ReviewCycleSettings> & {
  title?: string;
  form?: ReviewForm;
  /** Create only, a manager evaluation: also create its self evaluation. */
  withSelfCycle?: boolean;
  /** Set on create only, from the template (PF-9). */
  kind?: ReviewCycleKind;
};

export interface DuplicateReviewCyclePayload {
  title: string;
}

/** `active` = draft or launched, `past` = locked. */
export type ReviewCyclePhase = 'active' | 'past';

export interface ReviewCycleFilters {
  page?: number;
  limit?: number;
  search?: string;
  cycleStatus?: ReviewCycleStatus;
  phase?: ReviewCyclePhase;
  /** true = one-time cycles only (no automatic evaluation's cycles). */
  excludePrograms?: boolean;
}

// ─── Participants ──────────────────────────────────────────────────────────

export interface ReviewSubmissionSummary {
  id: string;
  reviewerType: ReviewerType;
  status: ReviewSubmissionStatus;
  submittedAt: string | null;
  score: number | null;
  nominatedBy: ReviewPeerNominator | null;
  /** Null for an anonymous peer outside HR's view. */
  reviewer: PerformancePerson | null;
}

export interface ReviewParticipant {
  reviewId: string;
  employee: PerformancePerson | null;
  manager: PerformancePerson | null;
  status: ReviewStatus;
  sharedAt: string | null;
  acknowledgedAt: string | null;
  /** The employee's optional comment when acknowledging (PF-6). */
  acknowledgementComment: string | null;
  selfScore: number | null;
  managerScore: number | null;
  peerScore: number | null;
  submissions: ReviewSubmissionSummary[];
  /**
   * Team feedback only (U3): how many reports were asked and how many
   * answered. Never who: their submissions are not in `submissions`.
   */
  teamResponses?: { invited: number; submitted: number } | null;
  /** Team feedback: when HR shared the results with the managers above. */
  sharedWithManagersAt?: string | null;
}

/** HR's view of one review: every submission with its answers. */
export interface ReviewParticipantDetail extends ReviewParticipant {
  cycle: ReviewCycle;
  form: ReviewForm;
  submissions: (ReviewSubmissionSummary & { answers: ReviewAnswer[] })[];
}

export interface SetReviewManagerPayload {
  /** Any active employee other than the reviewee (PF-2), or null. */
  managerEmployeeId: string | null;
}

/** The full peer list: peers left out are removed if they have not started. */
export interface SetReviewPeersPayload {
  peerEmployeeIds: string[];
}

export interface RemindReviewersResult {
  remindedCount: number;
}

export interface AddReviewParticipantsResult {
  addedCount: number;
}

/** A department as the add-participants picker lists it. */
export interface PerformanceDepartment {
  id: string;
  name: string;
  /** Active employees in it. */
  employeeCount: number;
}

export interface AddReviewParticipantsPayload {
  employeeIds?: string[];
  departmentIds?: string[];
  allEmployees?: boolean;
  /** Team feedback: every active employee with at least one active report. */
  allManagers?: boolean;
}

export interface ReviewCycleResults {
  participantCount: number;
  byStatus: Record<ReviewStatus, number>;
  averages: {
    self: number | null;
    manager: number | null;
    peer: number | null;
  };
  /** Headline score (manager's, else self) rounded into 1..5 bands. */
  distribution: { band: number; count: number }[];
  questions: {
    questionId: string;
    title: string;
    type: ReviewQuestionType;
    scaleMax: number | null;
    byType: {
      reviewerType: ReviewerType;
      responseCount: number;
      average?: number | null;
      optionCounts?: { optionId: string; count: number }[];
    }[];
  }[];
  participants: Pick<
    ReviewParticipant,
    | 'reviewId'
    | 'employee'
    | 'manager'
    | 'status'
    | 'selfScore'
    | 'managerScore'
    | 'peerScore'
  >[];
}

// ─── Manager (either portal, as an employee) ───────────────────────────────

export interface ManagerReviewGroup {
  cycle: ReviewCycle;
  reviews: ReviewParticipant[];
}

export interface ManagerReviewPeer {
  status: ReviewSubmissionStatus;
  nominatedBy: ReviewPeerNominator | null;
  employee: PerformancePerson | null;
}

export interface ManagerReviewDetail {
  cycle: ReviewCycle;
  review: ReviewParticipant;
  canShare: boolean;
  editable: boolean;
  /** False while the self review is PENDING (PF-4): drafts still save. */
  canSubmit: boolean;
  selfReview: { state: SelfReviewState; dueDate: string | null };
  canManagePeers: boolean;
  dueDate: string | null;
  mySubmission: ReviewSubmissionBlock | null;
  /** Submitted self and peer reviews (the self one only once submitted). */
  otherSubmissions: ReviewSubmissionBlock[];
  /** The reviewee's goals for a GOALS question, until submitted. */
  revieweeGoals: ReviewGoalItem[];
  peers: ManagerReviewPeer[];
}

// ─── Employee portal ───────────────────────────────────────────────────────

export interface ReviewCycleSummary {
  id: string;
  title: string;
  description: string | null;
  status: ReviewCycleStatus;
  kind: ReviewCycleKind;
  periodStart: string | null;
  periodEnd: string | null;
  /** Locked, or past its auto-lock date: nothing can change any more. */
  locked: boolean;
}

export interface EmployeeReviewTask {
  submissionId: string;
  reviewId: string;
  reviewerType: ReviewerType;
  status: ReviewSubmissionStatus;
  submittedAt: string | null;
  dueDate: string | null;
  editable: boolean;
  cycle: ReviewCycleSummary;
  reviewee: PerformancePerson | null;
}

export interface EmployeeSharedReviewItem {
  reviewId: string;
  cycle: ReviewCycleSummary;
  manager: PerformancePerson | null;
  sharedAt: string;
  acknowledgedAt: string | null;
  requireAcknowledgement: boolean;
}

export interface EmployeePeerNomination {
  reviewId: string;
  cycle: ReviewCycleSummary;
  peersPerReviewee: number;
  peers: {
    status: ReviewSubmissionStatus;
    employee: PerformancePerson | null;
  }[];
}

export interface EmployeeReviewsOverview {
  toWrite: EmployeeReviewTask[];
  sharedWithMe: EmployeeSharedReviewItem[];
  peerNominations: EmployeePeerNomination[];
}

export interface EmployeeReviewSubmission {
  cycle: ReviewCycleSummary;
  reviewee: PerformancePerson | null;
  reviewId: string;
  dueDate: string | null;
  editable: boolean;
  peerAnonymous: boolean;
  /**
   * Team feedback on a manager (U8): set to the company's threshold, the
   * form says the answers are anonymous and only shown grouped from it.
   */
  teamFeedbackThreshold?: number | null;
  submission: RevieweeSubmissionBlock;
  /** My goals for a GOALS question, until submitted. */
  revieweeGoals: ReviewGoalItem[];
}

export interface EmployeeSharedReview {
  reviewId: string;
  cycle: ReviewCycleSummary;
  manager: PerformancePerson | null;
  sharedAt: string;
  acknowledgedAt: string | null;
  acknowledgementComment: string | null;
  requireAcknowledgement: boolean;
  /** Own self-review first, then the manager's, then peers when shared. */
  submissions: RevieweeSubmissionBlock[];
}

/** One team member's review, for a manager above them (My team, A8). */
export interface MemberReviewItem {
  reviewId: string;
  cycle: ReviewCycleSummary;
  status: ReviewStatus;
  manager: PerformancePerson | null;
  sharedAt: string | null;
  acknowledgedAt: string | null;
  /** Once shared only. */
  acknowledgementComment: string | null;
  /** Shared reviews, or the viewer's own review to write. */
  managerScore: number | null;
  dueDate: string | null;
  /** The viewer writes this one: open it from their review screen. */
  isReviewer: boolean;
}

/** A shared review of a team member, read-only, with scores (managers). */
export interface MemberSharedReview {
  reviewId: string;
  cycle: ReviewCycleSummary;
  employee: PerformancePerson | null;
  manager: PerformancePerson | null;
  sharedAt: string;
  acknowledgedAt: string | null;
  acknowledgementComment: string | null;
  submissions: ReviewSubmissionBlock[];
}

export interface AcknowledgeReviewPayload {
  /** Optional, up to 1,000 characters (PF-6). */
  comment?: string;
}

/** Whether the employee portal shows Performance (PF-21). */
export interface PerformanceSummary {
  enabled: boolean;
}

// ─── 1:1s ──────────────────────────────────────────────────────────────────

export interface OneOnOneListItem {
  id: string;
  cadence: OneOnOneCadence;
  durationMinutes: number;
  archivedAt: string | null;
  createdAt: string;
  /** The caller's side in this series (PF-10). */
  myRole: OneOnOneRole;
  /**
   * Whether the host (`manager`) is above the employee in the reporting
   * chain; false when HR runs the series.
   */
  hostIsManager?: boolean;
  employee: PerformancePerson | null;
  /** The person who runs the series: a manager above, or HR. */
  manager: PerformancePerson | null;
  nextMeeting: {
    id: string;
    scheduledAt: string;
    talkingPointCount: number;
  } | null;
  lastMeeting: { id: string; scheduledAt: string } | null;
  openActionItemCount: number;
}

export interface OneOnOneActionItem {
  id: string;
  meetingId: string | null;
  text: string;
  assigneeRole: OneOnOneRole;
  dueDate: string | null;
  isDone: boolean;
  completedAt: string | null;
  createdByRole: OneOnOneRole;
  createdAt: string;
}

export interface OneOnOneMeetingSummary {
  id: string;
  scheduledAt: string;
  status: OneOnOneMeetingStatus;
  completedAt: string | null;
  hasSummary: boolean;
  talkingPointCount: number;
}

export interface OneOnOneDetail {
  id: string;
  cadence: OneOnOneCadence;
  durationMinutes: number;
  archivedAt: string | null;
  createdAt: string;
  myRole: OneOnOneRole;
  employee: PerformancePerson | null;
  manager: PerformancePerson | null;
  /** Newest first. */
  meetings: OneOnOneMeetingSummary[];
  actionItems: OneOnOneActionItem[];
}

export interface OneOnOneTalkingPoint {
  id: string;
  text: string;
  authorRole: OneOnOneRole;
  isMine: boolean;
  isDone: boolean;
  /** Moved over from an earlier meeting that ended with it unchecked. */
  carriedOver: boolean;
  createdAt: string;
}

export interface OneOnOneMeeting {
  id: string;
  oneOnOneId: string;
  scheduledAt: string;
  durationMinutes: number;
  status: OneOnOneMeetingStatus;
  completedAt: string | null;
  sharedNotes: string | null;
  summary: string | null;
  /** The caller's own private note. Never the other person's. */
  privateNote: string | null;
  myRole: OneOnOneRole;
  previousMeetingId: string | null;
  nextMeetingId: string | null;
  talkingPoints: OneOnOneTalkingPoint[];
  /** Open items from any meeting, plus everything raised in this one. */
  actionItems: OneOnOneActionItem[];
}

export interface CloseOneOnOneMeetingResult {
  meeting: OneOnOneMeeting;
  nextMeetingId: string | null;
}

export interface CreateOneOnOnePayload {
  employeeId: string;
  cadence: OneOnOneCadence;
  /** ISO instant. */
  firstMeetingAt: string;
  durationMinutes?: number;
  talkingPoints?: string[];
}

/** HR starts 1:1s for a manager and someone in their subtree. */
export interface CreateOneOnOnePairPayload extends CreateOneOnOnePayload {
  managerEmployeeId: string;
}

/**
 * A series as HR and managers above it see it: dates and cadence only, never
 * the content (PF-11).
 */
export interface OneOnOneMetadata {
  id: string;
  employee: PerformancePerson | null;
  manager: PerformancePerson | null;
  cadence: OneOnOneCadence;
  durationMinutes: number;
  archivedAt: string | null;
  createdAt: string;
  lastHeldAt: string | null;
  nextMeetingAt: string | null;
  heldCount: number;
}

/** My team member tab: the series the viewer runs can be opened. */
export interface MemberOneOnOne extends OneOnOneMetadata {
  isParticipant: boolean;
}

export interface MemberPerformance {
  reviews: MemberReviewItem[];
  oneOnOnes: MemberOneOnOne[];
  probationReview: ProbationReviewInfo | null;
  /** The member's open individual goals (PF-17); editable by managers above. */
  goals: Goal[];
  /** How "Start probation review" starts one now (absent on older servers). */
  probationStart?: ProbationStartOptions;
}

/**
 * "Start probation review" in this company: with a self evaluation unless
 * the saved probation evaluations setting turned it off; `configured` once
 * that setting (and so its evaluation form) is saved.
 */
export interface ProbationStartOptions {
  selfReview: boolean;
  configured: boolean;
  /**
   * The company's end-of-probation automatic evaluation, whose form and
   * self evaluation a started review follows; the review is attached to it.
   */
  programId?: string | null;
}

/** The latest review of kind `probation` for an employee (spec 5.5). */
export interface ProbationReviewInfo {
  cycleId: string;
  reviewId: string;
  cycleStatus: ReviewCycleStatus;
  status: ReviewStatus;
  selfDueDate: string | null;
  managerDueDate: string | null;
  sharedAt: string | null;
  managerEmployeeId: string | null;
}

/** Both dates optional: self in 7 days, manager 5 days before the end. */
export interface StartProbationReviewPayload {
  selfDueDate?: string;
  managerDueDate?: string;
}

export interface StartProbationReviewResult {
  cycleId: string;
  reviewId: string;
  /** Null when the company's probation evaluations have no self review. */
  selfDueDate: string | null;
  managerDueDate: string;
}

/** One review on the company portal profile > Performance tab. */
export interface EmployeeProfileReview {
  reviewId: string;
  cycle: {
    id: string;
    title: string;
    kind: ReviewCycleKind;
    status: ReviewCycleStatus;
    periodStart: string | null;
    periodEnd: string | null;
    /** Set when an automatic evaluation created it. */
    programId: string | null;
    programName: string | null;
  };
  status: ReviewStatus;
  manager: PerformancePerson | null;
  sharedAt: string | null;
  acknowledgedAt: string | null;
  managerScore: number | null;
  /** Who fills it (null: several, a cycle from before 2026-10-29). */
  reviewer: EvaluationReviewer | null;
  selfScore: number | null;
}

/** Company portal profile > Performance (HR, PF-11: 1:1 dates only). */
export interface EmployeeProfilePerformance {
  reviews: EmployeeProfileReview[];
  oneOnOnes: OneOnOneMetadata[];
  probationReview: ProbationReviewInfo | null;
  goals: Goal[];
  /** How "Start probation review" starts one now. */
  probationStart?: ProbationStartOptions;
}

// ─── Goals ─────────────────────────────────────────────────────────────────

export interface Goal {
  id: string;
  level: GoalLevel;
  title: string;
  description: string | null;
  measure: GoalMeasure;
  startValue: number;
  targetValue: number | null;
  currentValue: number;
  unit: string | null;
  startDate: string | null;
  dueDate: string | null;
  status: GoalStatus;
  outcome: GoalOutcome | null;
  closedAt: string | null;
  lastCheckInAt: string | null;
  createdAt: string;
  /** 0 to 100. */
  progress: number;
  owner: PerformancePerson | null;
  parent: { id: string; title: string; level: GoalLevel } | null;
  /** Children the viewer can see, information only (PF-16). */
  aligned: { count: number; averageProgress: number } | null;
  checkInCount: number;
  canEdit: boolean;
  canDelete: boolean;
}

export interface GoalCheckIn {
  id: string;
  value: number;
  status: GoalStatus;
  comment: string | null;
  createdAt: string;
  author: { userId: string; name: string } | null;
}

/** Employee portal, My performance > Goals. */
export interface EmployeeGoalsOverview {
  individual: Goal[];
  team: Goal[];
  company: Goal[];
  /** The caller manages someone, so may own team goals. */
  canCreateTeamGoal: boolean;
}

export type GoalListState = 'open' | 'closed' | 'all';

export interface GoalFilters {
  level?: GoalLevel;
  ownerEmployeeId?: string;
  departmentId?: string;
  status?: GoalStatus;
  state?: GoalListState;
}

export interface GoalFieldsPayload {
  title: string;
  description?: string | null;
  measure?: GoalMeasure;
  startValue?: number;
  targetValue?: number | null;
  unit?: string | null;
  startDate?: string | null;
  dueDate?: string | null;
  parentGoalId?: string | null;
}

export interface CreateGoalPayload extends GoalFieldsPayload {
  level: GoalLevel;
  /** Defaults to the caller. */
  ownerEmployeeId?: string | null;
}

export type UpdateGoalPayload = Partial<GoalFieldsPayload>;

export interface GoalCheckInPayload {
  value: number;
  status: GoalStatus;
  comment?: string | null;
}

export interface CloseGoalPayload {
  outcome: GoalOutcome;
}

export interface GoalSettings {
  goalReminder: GoalReminderCadence;
}

export interface CreateOneOnOneMeetingPayload {
  /** ISO instant. */
  scheduledAt: string;
}

export interface UpdateOneOnOnePayload {
  cadence?: OneOnOneCadence;
  durationMinutes?: number;
}

export interface UpdateOneOnOneMeetingPayload {
  scheduledAt?: string;
  sharedNotes?: string | null;
  summary?: string | null;
  privateNote?: string | null;
}

export interface UpdateTalkingPointPayload {
  text?: string;
  isDone?: boolean;
}

export interface CreateActionItemPayload {
  text: string;
  assigneeRole: OneOnOneRole;
  dueDate?: string | null;
  meetingId?: string | null;
}

export interface UpdateActionItemPayload {
  text?: string;
  assigneeRole?: OneOnOneRole;
  dueDate?: string | null;
  isDone?: boolean;
}

// ─── Responses ─────────────────────────────────────────────────────────────

export type GetReviewTemplatesResponse = ApiResponse<ReviewTemplates>;
export type ReviewCustomTemplateResponse = ApiResponse<ReviewCustomTemplate>;
export type GetReviewCyclesResponse = GetListResponse<ReviewCycleListItem>;
export type ReviewCycleDetailResponse = ApiResponse<ReviewCycleDetail>;
export type GetReviewParticipantsResponse = ApiResponse<ReviewParticipant[]>;
export type ReviewParticipantResponse = ApiResponse<ReviewParticipant>;
export type ReviewParticipantDetailResponse =
  ApiResponse<ReviewParticipantDetail>;
export type ReviewCycleResultsResponse = ApiResponse<ReviewCycleResults>;
export type GetManagerReviewsResponse = ApiResponse<ManagerReviewGroup[]>;
export type ManagerReviewDetailResponse = ApiResponse<ManagerReviewDetail>;
export type PerformancePeopleResponse = ApiResponse<PerformancePerson[]>;
export type OneOnOneHostsResponse = ApiResponse<OneOnOneHost[]>;
export type PerformanceDepartmentsResponse = ApiResponse<
  PerformanceDepartment[]
>;
export type EmployeeReviewsOverviewResponse =
  ApiResponse<EmployeeReviewsOverview>;
export type EmployeeReviewSubmissionResponse =
  ApiResponse<EmployeeReviewSubmission>;
export type EmployeeSharedReviewResponse = ApiResponse<EmployeeSharedReview>;
export type GetOneOnOnesResponse = ApiResponse<OneOnOneListItem[]>;
export type OneOnOneDetailResponse = ApiResponse<OneOnOneDetail>;
export type OneOnOneMeetingResponse = ApiResponse<OneOnOneMeeting>;
export type CloseOneOnOneMeetingResponse =
  ApiResponse<CloseOneOnOneMeetingResult>;
export type OneOnOneActionItemResponse = ApiResponse<OneOnOneActionItem>;
export type OneOnOneMetadataListResponse = ApiResponse<OneOnOneMetadata[]>;
export type OneOnOneMetadataResponse = ApiResponse<OneOnOneMetadata>;
export type MemberPerformanceResponse = ApiResponse<MemberPerformance>;
export type EmployeeProfilePerformanceResponse =
  ApiResponse<EmployeeProfilePerformance>;
export type StartProbationReviewResponse =
  ApiResponse<StartProbationReviewResult>;
export type MemberSharedReviewResponse = ApiResponse<MemberSharedReview>;
export type PerformanceSummaryResponse = ApiResponse<PerformanceSummary>;
export type GoalResponse = ApiResponse<Goal>;
export type GoalsResponse = ApiResponse<Goal[]>;
export type GoalCheckInsResponse = ApiResponse<GoalCheckIn[]>;
export type EmployeeGoalsOverviewResponse = ApiResponse<EmployeeGoalsOverview>;
export type GoalSettingsResponse = ApiResponse<GoalSettings>;

/**
 * Where one automatic evaluation of one employee stands (spec 2026-10-28
 * v2, V2-8; evaluation programs 2026-10-29). Mirror of the backend
 * `ProbationEvaluationStatus`.
 */
export enum ProbationEvaluationStatus {
  UPCOMING = 'upcoming',
  SENT = 'sent',
  SUBMITTED = 'submitted',
  LATE = 'late',
  SHARED = 'shared',
  ACKNOWLEDGED = 'acknowledged',
  NO_MANAGER = 'no_manager',
}

// ─── Automatic evaluations (evaluation programs, spec 2026-10-29) ─────────

/** What an automatic evaluation's due date counts from. */
export enum EvaluationProgramAnchor {
  /** After the hire date (`employees.date_of_joining`). */
  HIRE = 'hire',
  /** Before the end of probation (the file's date, else the default length). */
  PROBATION_END = 'probation_end',
  /**
   * Days of the year ("MM-DD" in `fixedDates`), every year: it opens that
   * day (moved back to a working day). Team feedback programs only.
   */
  FIXED_DATE = 'fixed_date',
}

/**
 * Who fills an automatic evaluation (JP 2026-10-29): one reviewer per
 * program. A self evaluation is a program of its own, with the same or
 * another form, so each is easy to find and change.
 */
export enum EvaluationProgramReviewer {
  MANAGER = 'manager',
  /** The employee: a self evaluation, no manager involved. */
  SELF = 'self',
  /** A manager's team: anonymous team feedback on fixed dates. */
  TEAM = 'team',
}

/** The unit of an automatic evaluation's offset from its anchor. */
export enum EvaluationProgramOffsetUnit {
  DAYS = 'days',
  /** The company working calendar: weekly days off and day-off holidays. */
  WORKING_DAYS = 'working_days',
  MONTHS = 'months',
}

/** The server's limits for an automatic evaluation and the default probation. */
export const EVALUATION_PROGRAM_LIMITS = {
  nameMaxLength: 120,
  minOffset: 0,
  maxOffset: {
    [EvaluationProgramOffsetUnit.DAYS]: 1825,
    [EvaluationProgramOffsetUnit.WORKING_DAYS]: 260,
    [EvaluationProgramOffsetUnit.MONTHS]: 60,
  },
  minLeadDays: 0,
  maxLeadDays: 30,
  defaultLeadDays: 7,
  minDefaultProbationMonths: 1,
  maxDefaultProbationMonths: 24,
  defaultProbationMonths: 3,
  /** The upcoming counts and lists look this many days ahead. */
  horizonDays: 60,
} as const;

/**
 * The rules of an automatic evaluation: what `POST` / `PUT` send (with
 * `enabled`) and what the preview reads. The form is exactly one of
 * `templateId` (a company template) or `libraryKey` (a built-in). The
 * direction is implied by the anchor: after the hire date, before the end
 * of probation. `repeatYearly` only with anchor `hire`. Empty
 * `locationIds` / `departmentIds` = everyone.
 */
export interface EvaluationProgramRules {
  name: string;
  templateId: string | null;
  libraryKey: string | null;
  anchor: EvaluationProgramAnchor;
  offsetValue: number;
  offsetUnit: EvaluationProgramOffsetUnit;
  repeatYearly: boolean;
  leadDays: number;
  /** Who fills it. The default "End of probation" is always the manager's. */
  reviewer: EvaluationProgramReviewer;
  /** Anchor fixed_date: "MM-DD" days of the year, 1 to 4 (team feedback). */
  fixedDates: string[];
  /** Anchor fixed_date: answers due this many days after it opens. */
  responseDays: number;
  /** Team feedback: fewest active direct reports; null = the threshold. */
  minReports: number | null;
  locationIds: string[];
  departmentIds: string[];
}

/** Team feedback programs (spec 2026-10-29 upward feedback, U2). */
export const TEAM_PROGRAM_LIMITS = {
  maxFixedDates: 4,
  responseDays: { min: 1, max: 60, default: 14 },
  minReportsMax: 50,
} as const;

/** One run of a team feedback program (its Suivi): aggregates only. */
export interface EvaluationProgramTeamRun {
  runDate: string;
  openDate: string;
  dueDate: string;
  cycleId: string | null;
  cycleTitle: string | null;
  cycleStatus: ReviewCycleStatus | null;
  managers: number;
  invited: number;
  responses: number;
  responseRate: number | null;
}

/** The next run of a team feedback program, as the preview sees it. */
export interface EvaluationProgramTeamPreview {
  runDate: string;
  openDate: string;
  dueDate: string;
  /** It opens today: it goes at the next daily send. */
  opensToday: boolean;
  managers: number;
  /** Their active direct reports: who is asked. */
  employees: number;
}

/** `POST` / `PUT .../performance/evaluation-programs[/:programId]`. */
export interface EvaluationProgramPayload extends EvaluationProgramRules {
  enabled: boolean;
}

/** One automatic evaluation's evaluations, by group (next 60 days ahead). */
export interface EvaluationProgramCounts {
  /** Not created yet, sent within the next 60 days (incl. no manager). */
  upcoming: number;
  /** Created, not done yet (late ones included). */
  inProgress: number;
  /** Created, past its due date, not submitted or shared. */
  late: number;
  /** Shared with the employee (acknowledged or not); a self evaluation once submitted. */
  done: number;
}

/** `GET .../performance/evaluation-programs[/:programId]`. */
export interface EvaluationProgram extends EvaluationProgramPayload {
  id: string;
  /** The form's name, in the caller's language for a built-in one. */
  templateName: string;
  /**
   * The company's "End of probation" one, created switched off for every
   * company: it cannot be deleted (turn it off instead).
   */
  isDefault: boolean;
  createdAt: string;
  updatedAt: string;
  counts: EvaluationProgramCounts;
}

/**
 * Where one employee's evaluation of an automatic evaluation stands: the
 * same values as {@link ProbationEvaluationStatus}. Groups: upcoming =
 * `upcoming` + `no_manager`; in progress = `sent` + `submitted` + `late`;
 * done = `shared` + `acknowledged`.
 */
export type EvaluationStatus = ProbationEvaluationStatus;

/** `?status=` of the evaluations list (none = every group). */
export enum EvaluationProgramStatusFilter {
  UPCOMING = 'upcoming',
  IN_PROGRESS = 'in_progress',
  LATE = 'late',
  DONE = 'done',
}

/** The manager's probation decision on a submitted evaluation. */
export interface EvaluationDecision {
  outcome: ProbationDecisionOutcome;
  /** `extend`: the proposed new end date, YYYY-MM-DD. */
  date: string | null;
}

/** One row of `GET .../evaluation-programs/:programId/evaluations`. */
export interface EvaluationProgramEvaluation {
  /** `employeeId:dueDate`, unique in the list. */
  key: string;
  employee: PerformancePerson | null;
  manager: PerformancePerson | null;
  hireDate: string | null;
  /** The probation end the dates were counted from (anchor probation_end). */
  probationEndDate: string | null;
  /** YYYY-MM-DD: when it goes (upcoming) or went to the manager. */
  sendDate: string;
  dueDate: string;
  /** Upcoming or no manager: the last day it can still go out. */
  closesOn: string | null;
  status: EvaluationStatus;
  /** Set once the evaluation exists: links to the participant page. */
  cycleId: string | null;
  reviewId: string | null;
  decision: EvaluationDecision | null;
}

/** `GET .../evaluation-programs/:programId/evaluations`. */
export interface EvaluationProgramEvaluationList {
  /** Company-local today and the day of the next daily send. */
  today: string;
  nextSendOn: string;
  horizonDays: number;
  items: EvaluationProgramEvaluation[];
}

/** `POST .../evaluation-programs/preview`: the rules being edited. */
export interface EvaluationProgramPreviewPayload extends EvaluationProgramRules {
  /** The program being edited: its evaluations already created count. */
  programId?: string | null;
}

/** One evaluation the next daily send would create. */
export interface EvaluationProgramPreviewRow {
  employee: PerformancePerson | null;
  /** Null = no manager: nothing goes, HR is told (a self evaluation: always null, it goes to the employee). */
  manager: PerformancePerson | null;
  sendDate: string;
  dueDate: string;
  probationEndDate: string | null;
}

/**
 * The worked example for a hypothetical hire today (company-local),
 * computed with the same rule as the sweep. `*MovedFrom`: the date before
 * it was moved back to a working day (null = not moved).
 */
export interface EvaluationProgramExample {
  hireDate: string;
  /** Anchor probation_end: the end of probation at the default length. */
  probationEndDate: string | null;
  sendDate: string;
  sendMovedFrom: string | null;
  dueDate: string;
  dueMovedFrom: string | null;
}

/** `POST .../evaluation-programs/preview`. */
export interface EvaluationProgramPreview {
  /** Who these rules send to: `managers` below counts employees for `self`. */
  reviewer: EvaluationProgramReviewer;
  /** YYYY-MM-DD of the next daily send, and company-local today. */
  sendOn: string;
  today: string;
  /** What the next daily send would create if switched on now (my locations). */
  sendToday: EvaluationProgramPreviewRow[];
  /** Company-wide: evaluations that would go, to how many managers (or employees). */
  evaluations: number;
  managers: number;
  /** Company-wide: due to go but without a manager, so not sent. */
  noManager: number;
  /** Counted above but outside the viewer's locations. */
  hiddenCount: number;
  example: EvaluationProgramExample;
  /** Company-wide: evaluations it would send in the next 60 days. */
  nextSixtyDays: number;
  /** A team feedback program: its next run (null: no date ahead). */
  team?: EvaluationProgramTeamPreview | null;
}

/** `GET .../employees/:employeeId/performance/upcoming-evaluations`. */
export interface EmployeeUpcomingEvaluation {
  programId: string;
  programName: string;
  sendDate: string;
  dueDate: string;
  closesOn: string | null;
  probationEndDate: string | null;
  /** Null for a self evaluation: the employee fills it. */
  manager: PerformancePerson | null;
  reviewer: EvaluationProgramReviewer;
  status: EvaluationStatus;
}

export type EvaluationProgramsResponse = ApiResponse<EvaluationProgram[]>;
export type EvaluationProgramResponse = ApiResponse<EvaluationProgram>;
export type EvaluationProgramEvaluationListResponse =
  ApiResponse<EvaluationProgramEvaluationList>;
export type EvaluationProgramPreviewResponse =
  ApiResponse<EvaluationProgramPreview>;
export type EmployeeUpcomingEvaluationsResponse = ApiResponse<
  EmployeeUpcomingEvaluation[]
>;

// ─── Team feedback on managers (upward evaluation, spec 2026-10-29) ───────

/**
 * U3: the fewest answers before any team feedback is shown, per company
 * (default 3). Under it, only the counts: "Not enough answers to protect
 * anonymity".
 */
export const UPWARD_MIN_RESPONSES = { min: 3, max: 10, default: 3 } as const;

/** U6: a score under this (out of 5) plus a team turnover above the company's
 *  flags the manager as needing attention. */
export const UPWARD_ATTENTION_SCORE = 3;

/** The built-in "Feedback on my manager" template (U5). */
export const UPWARD_TEMPLATE_KEY = 'upward_manager';

export interface UpwardFeedbackSettings {
  minResponses: number;
}

/** A rating question's grouped answers. */
export interface UpwardRatingResult {
  questionId: string;
  title: string;
  type: ReviewQuestionType.RATING;
  scaleMax: number;
  scaleLabels: string[] | null;
  responseCount: number;
  average: number | null;
  /** Answers per level, level 1 first. */
  distribution: number[];
  /** Comments left beside the rating, in random order, never named. */
  comments: string[];
}

export interface UpwardChoiceResult {
  questionId: string;
  title: string;
  type: ReviewQuestionType.MULTIPLE_CHOICE;
  responseCount: number;
  options: { optionId: string; label: string; count: number }[];
}

export interface UpwardTextResult {
  questionId: string;
  title: string;
  type: ReviewQuestionType.TEXT;
  /** In random order, never named. */
  comments: string[];
}

export type UpwardQuestionResult =
  UpwardRatingResult | UpwardChoiceResult | UpwardTextResult;

/**
 * One manager's team feedback, grouped (U3). Never says who answered: no
 * reviewer, no submission id, no date per answer. Under the threshold only
 * the counts are there (`belowThreshold`, no score, no questions).
 */
export interface UpwardFeedbackResult {
  reviewId: string;
  cycle: {
    id: string;
    title: string;
    status: ReviewCycleStatus;
    launchedAt: string | null;
    lockedAt: string | null;
    upwardDueDate: string | null;
  };
  manager: PerformancePerson | null;
  threshold: number;
  invited: number;
  responses: number;
  /** responses / invited x 100, rounded; null when nobody was asked. */
  responseRate: number | null;
  belowThreshold: boolean;
  /** Mean of the rating answers on a 5-point scale. */
  score: number | null;
  questions: UpwardQuestionResult[];
  sharedAt: string | null;
  sharedWithManagersAt: string | null;
}

/** U6: one manager in the HR comparison. Turnover = Analytics > Turnover. */
export interface UpwardComparisonRow {
  reviewId: string;
  manager: PerformancePerson | null;
  invited: number;
  responses: number;
  responseRate: number | null;
  belowThreshold: boolean;
  score: number | null;
  /** Active direct reports today (the analytics closing headcount). */
  teamSize: number;
  /** Departures from their direct team in the last 12 months. */
  departures: number;
  /** Team turnover, last 12 months (departures / average headcount x 100). */
  turnoverRate: number | null;
  needsAttention: boolean;
  sharedAt: string | null;
  sharedWithManagersAt: string | null;
}

export interface UpwardComparison {
  cycleId: string;
  threshold: number;
  attentionScore: number;
  /** The analytics period the turnover numbers cover. */
  period: { from: string; to: string };
  companyTurnoverRate: number | null;
  rows: UpwardComparisonRow[];
}

/** Who HR shares a manager's team feedback with (U4). */
export interface UpwardSharePayload {
  /** The manager the feedback is about. */
  manager?: boolean;
  /** The managers above them. */
  managers?: boolean;
}

/** U7: one period of a manager's team feedback on their profile (HR). */
export interface UpwardProfilePeriod {
  reviewId: string;
  cycleId: string;
  title: string;
  status: ReviewCycleStatus;
  launchedAt: string | null;
  lockedAt: string | null;
  invited: number;
  responses: number;
  belowThreshold: boolean;
  score: number | null;
}

export interface UpwardProfileFeedback {
  threshold: number;
  periods: UpwardProfilePeriod[];
}

/** Employee portal: team feedback shared with me, about me or a manager below me. */
export interface EmployeeUpwardResultItem {
  reviewId: string;
  cycle: ReviewCycleSummary;
  manager: PerformancePerson | null;
  aboutMe: boolean;
  sharedAt: string;
}

export type UpwardFeedbackSettingsResponse =
  ApiResponse<UpwardFeedbackSettings>;
export type UpwardFeedbackResultResponse = ApiResponse<UpwardFeedbackResult>;
export type UpwardComparisonResponse = ApiResponse<UpwardComparison>;
export type UpwardProfileFeedbackResponse = ApiResponse<UpwardProfileFeedback>;
export type EmployeeUpwardResultsResponse = ApiResponse<
  EmployeeUpwardResultItem[]
>;
